import React, { Fragment, useState } from "react";
import { Button } from "react-admin";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
} from "@material-ui/core";
import SubcontractorAdjustmentPriceStatusLogTable from "./SubcontractorAdjustmentPriceStatusLogTable";
import { StyledButton } from "../../../components/ListShowButton";
import SanitizedGrid from "components/SanitizedGrid";
import capitalize from "lodash/capitalize";

type AdjustmentPriceStatus =
  | "Approved"
  | "Archived"
  | "Unapproved"
  | "Rejected";

interface ISubcontractorAdjustmentPriceLogButton {
  subcontractorAdjustmentPriceId: number;
  routeId: number;
  subcontractorId: number;
  filterStatus: AdjustmentPriceStatus;
  currentStatus: AdjustmentPriceStatus;
}

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "auto",
    },
  },
});

const LogButton = ({
  subcontractorAdjustmentPriceId,
  routeId,
  subcontractorId,
  filterStatus,
  currentStatus,
}: ISubcontractorAdjustmentPriceLogButton) => {
  const [open, setOpen] = useState<boolean>(false);

  const classes = useStyles();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Mapper for table Title based on currentStatus or filterStatus value and table position
  // (1: selected adjustment price, 2: related adjustment prices)
  const tableTitleMapper: Record<
    1 | 2,
    Record<AdjustmentPriceStatus, string>
  > = {
    1: {
      Unapproved: "Unapproved Adjustment Price Status History",
      Approved: "Approved Adjustment Price Status History",
      Rejected: "Rejected Adjustment Price Status History",
      Archived: "Archived Adjustment Price Status History",
    },
    2: {
      Unapproved: "",
      Approved: "Active Adjustment Price",
      Rejected: "",
      Archived: "Archived Adjustment Price Status History",
    },
  };

  // Mapper for table Remark based on currentStatus or filterStatus value and table position
  // (1: selected adjustment price, 2: related adjustment prices)
  const tableRemarkMapper: Record<
    1 | 2,
    Record<AdjustmentPriceStatus, string>
  > = {
    1: {
      Unapproved: "",
      Approved: "",
      Rejected: "",
      Archived: "",
    },
    2: {
      Unapproved: "",
      Approved: "This value is the current active adjustment price",
      Rejected: "",
      Archived: "This value was previously approved and is no longer in use",
    },
  };

  return (
    <Fragment>
      <StyledButton
        variant="outlined"
        style={{ color: "#1976d2", borderColor: "#1976d2" }}
        onClick={handleOpenDialog}
      >
        Status History
      </StyledButton>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">
          Subcontractor Adjustment Price Status History
        </DialogTitle>
        <DialogContent>
          <SanitizedGrid container spacing={4}>
            {/* Table 1: Status log for selected adjustment price */}
            <Grid item xs={12}>
              <SubcontractorAdjustmentPriceStatusLogTable
                basePath="subcontractorAdjustmentPriceStatusLogs"
                filter={{
                  SubcontractorAdjustmentPriceId:
                    subcontractorAdjustmentPriceId,
                }}
                title={
                  tableTitleMapper[1][
                    capitalize(currentStatus) as AdjustmentPriceStatus
                  ]
                }
              />
            </Grid>

            {/* Table 2: List of other related adjustment prices by route & subcontractor */}
            <Grid item xs={12}>
              <SubcontractorAdjustmentPriceStatusLogTable
                basePath="subcontractorAdjustmentPrices"
                filter={{
                  Status: filterStatus,
                  RouteId: routeId,
                  SubcontractorId: subcontractorId,
                }}
                loggedAtSource="updatedAt"
                showOldAndNewStatus={false}
                showPagination={filterStatus !== "Approved"} // If showing current active adjustment price, hide pagination
                title={tableTitleMapper[2][filterStatus]}
                staticRemark={tableRemarkMapper[2][filterStatus]}
                pageSize={filterStatus === "Approved" ? 1 : 10} // If showing current active adjustment price, show only 1 row
              />
            </Grid>
          </SanitizedGrid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" label="Back" />
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default LogButton;
